import { useTranslation } from 'next-i18next';
import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import { Container } from 'components/domain/container';
import type { IAdventureTravelRouteProduct } from 'components/domain/fdr-adventure-travel-routes';
import { NavigationIds } from 'components/domain/fdr-geo/navigation/fdr-geo-navigation.types';
import FdrGeoTripsPinsProducts from 'components/domain/fdr-geo/pins/fdr-geo-trips-pins-products';
import {
  enrichRelatedPinWithIds,
  findUniqProductRoutePins,
  RelatedRoundtrips,
  TProductPin,
  TRelatedPin,
} from 'components/domain/fdr-geo/pins/fdr-geo-trips-pins.utils';
import FdrGeoProductsRoute from 'components/domain/fdr-geo/route/fdr-geo-products-route';
import FdrGeoTripsList from 'components/domain/fdr-geo/trips-list/fdr-geo-trips-list';
import { useFullScreenControl } from 'components/domain/fdr-map';
import FdrGeoTripsFullscreenButton from './fdr-geo-trips-fullscreen-button';
import FdrGeoTripsLegend from './fdr-geo-trips-legend';
import FdrGeoTripsMap from './fdr-geo-trips-map';
import FdrResetMapButton from './fdr-geo-trips-reset-map-button';
import { FdrGeoTripsContext } from './fdr-geo-trips.context';

const Holder = styled.div(
  sx2CssThemeFn(({ theme: { colors, space } }) => ({
    background: colors.paperGrey,
    padding: [`${space[3]} 0 40px 0`, '80px 0'],
  }))
);

const MapContainer = styled(Container)<{ height: number; isB2b: boolean }>(
  sx2CssThemeFn(({ height, isB2b }) => ({
    display: 'grid',
    gridTemplateColumns: ['1fr', '369px 1fr'],
    gridTemplateRows: isB2b
      ? [`auto ${height}px auto`, `${height}px 45px`]
      : [`auto ${height}px auto`, `calc(${height}px - 16px) 0`],
    columnGap: '20px',
    rowGap: 3,
  }))
);

const SectionMainTitle = styled.h2(
  sx2CssThemeFn(({ theme: { media, space, fontSizes } }) => ({
    fontSize: '46px',
    fontWeight: '800',
    lineHeight: '60px',
    textAlign: 'center',
    marginBottom: space[6],
    marginTop: [6, 0],
    [media.mobile]: {
      fontSize: fontSizes[7],
      lineHeight: '42px',
    },
  }))
);

interface IFdrGeoTripsProps {
  products: IAdventureTravelRouteProduct[];
  related: RelatedRoundtrips[];
  isB2b: boolean;
  country: string;
  id: string;
}

const MAP_HEIGHT_MOBILE = 310;
const MAP_HEIGHT_DESKTOP = 620;

const FdrGeoTrips: React.FC<IFdrGeoTripsProps> = ({
  products,
  related: prevRelated,
  country,
  id,
  isB2b,
}: IFdrGeoTripsProps) => {
  const [t] = useTranslation('geo');
  const { mobile } = useDeviceType();
  const [activeRoutePoint, setActiveRoutePoint] = useState(null);
  const [activeProduct, setActiveProduct] = useState(null);
  const [clusterMarkers, setClusterMarkers] = useState(null);
  const [activeTrip, setActiveTrip] = useState(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const { isFullScreen, setIsFullScreen } =
    useFullScreenControl(mapContainerRef);

  const routePoints: Map<string, TRelatedPin> = useMemo(
    () => enrichRelatedPinWithIds(prevRelated),
    [prevRelated]
  );

  const productPins: Map<string, TProductPin> = useMemo(
    () => findUniqProductRoutePins(products),
    [products]
  );

  const resetMap = () => {
    setActiveRoutePoint(null);
    setActiveProduct(null);
    setClusterMarkers(null);
    setActiveTrip(null);
  };

  return (
    <FdrGeoTripsContext.Provider
      value={{
        activeRoutePoint,
        activeProduct,
        setClusterMarkers,
        clusterMarkers,
        setActiveRoutePoint,
        setActiveProduct,
        activeTrip,
        setActiveTrip,
        routePoints,
        productPins,
        isFullScreen,
      }}
    >
      <Holder id={NavigationIds.TRIPS}>
        <SectionMainTitle>
          {t(`map.tripList.title`, { country })}
        </SectionMainTitle>
        <MapContainer
          height={mobile ? MAP_HEIGHT_MOBILE : MAP_HEIGHT_DESKTOP}
          isB2b={isB2b}
        >
          <FdrGeoTripsList />
          <FdrGeoTripsMap
            height={mobile ? MAP_HEIGHT_MOBILE : MAP_HEIGHT_DESKTOP}
            key="map"
            ref={mapContainerRef}
          >
            <FdrGeoTripsPinsProducts id={id} />
            <FdrGeoProductsRoute />
            <FdrGeoTripsFullscreenButton onChange={setIsFullScreen} />
            <FdrResetMapButton onClick={resetMap} />
          </FdrGeoTripsMap>
          {isB2b ? <FdrGeoTripsLegend /> : null}
        </MapContainer>
      </Holder>
    </FdrGeoTripsContext.Provider>
  );
};

export default FdrGeoTrips;
